import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import type { reportCategories_reportsOverviewPage$key } from '@atlassian/jira-relay/src/__generated__/reportCategories_reportsOverviewPage.graphql';
import { ReportCategory } from '../report-category/index.tsx';

type OverviewPageProps = {
	reportCategoriesData: reportCategories_reportsOverviewPage$key;
};

export const ReportCategories = ({ reportCategoriesData }: OverviewPageProps) => {
	const data = useFragment<reportCategories_reportsOverviewPage$key>(
		graphql`
			fragment reportCategories_reportsOverviewPage on Query {
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectKey)
					@skip(if: $isBoardContext)
					@required(action: THROW) {
					reportCategories @optIn(to: "JiraReportCategories") @required(action: THROW) {
						edges @required(action: THROW) {
							node {
								name @required(action: THROW)
								...reportCategory_reportsOverviewPage
							}
						}
					}
				}
				jira @include(if: $isBoardContext) @required(action: THROW) {
					jiraBoard(id: $boardId) @required(action: THROW) {
						... on JiraBoard {
							reportCategories @optIn(to: "JiraReportCategories") @required(action: THROW) {
								edges @required(action: THROW) {
									node {
										name @required(action: THROW)
										...reportCategory_reportsOverviewPage
									}
								}
							}
						}
					}
				}
			}
		`,
		reportCategoriesData,
	);

	let categories;

	if (data.jira_projectByIdOrKey) {
		categories = data.jira_projectByIdOrKey.reportCategories.edges.flatMap((categoryEdge) =>
			categoryEdge?.node ? [categoryEdge.node] : [],
		);
	} else if (data.jira) {
		categories = data.jira.jiraBoard.reportCategories?.edges.flatMap((categoryEdge) =>
			categoryEdge?.node ? [categoryEdge.node] : [],
		);
	}

	if (!categories) {
		throw new Error('reportCategoriesQuery returned no data');
	}

	const shouldShowHeading = categories && categories.length > 1;

	return (
		<>
			{categories.map((category) => (
				<JSErrorBoundary
					key={category.name}
					id="reportsOverviewPageReportCategory"
					packageName="jiraReportsOverviewPage"
					teamName="helios-reports"
					fallback="unmount"
				>
					<ReportCategory category={category} shouldShowHeading={shouldShowHeading} />
				</JSErrorBoundary>
			))}
		</>
	);
};
